import { useCallback, useEffect, useState } from "react";
import { KeyRound, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { ResponsiveTable } from "@/components/ResponsiveTable";
import { PageHeader } from "@/components/PageHeader";
import { useConfirm } from "@/components/ConfirmDialog";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/datetime";

export default function AuthStatesPage() {
  const [states, setStates] = useState(null);
  const confirm = useConfirm();

  const load = useCallback(() => {
    api.get("/auth-states").then((r) => setStates(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (s) => {
    const ok = await confirm({
      title: "Delete this login state?",
      description: `"${s.name}" will be removed. Tests still attached to it will run logged out next time.`,
      confirmLabel: "Delete",
      danger: true,
      testId: "confirm-delete-auth-state",
    });
    if (!ok) return;
    try {
      await api.delete(`/auth-states/${s.id}`);
      toast.success("Login state deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const columns = [
    {
      key: "name",
      label: "Name",
      mobileVariant: "title",
      render: (s) => <span className="rt-primary">{s.name}</span>,
    },
    {
      key: "origin",
      label: "Captured at URL",
      render: (s) => (
        <span className="rt-value font-mono text-xs text-[#64748B] block max-w-[260px] truncate">{s.origin}</span>
      ),
    },
    {
      key: "created_at",
      label: "Created",
      render: (s) => (
        <span className="text-xs text-[#64748B]">{formatDateTime(s.created_at)}</span>
      ),
    },
    {
      key: "actions",
      label: "Actions",
      align: "right",
      mobileVariant: "actions",
      render: (s) => (
        <button
          onClick={() => handleDelete(s)}
          data-testid={`delete-auth-state-${s.id}`}
          className="inline-flex items-center justify-center w-8 h-8 rounded-md border border-[#E5E7EB] text-[#64748B] hover:text-[#B91C1C] hover:border-[#FCA5A5] hover:bg-[#FEF2F2] transition-colors"
          aria-label="Delete"
        >
          <Trash2 size={13} />
        </button>
      ),
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 py-8 sm:py-10" data-testid="auth-states-page">
      <PageHeader
        title="Login States"
        description="Encrypted browser sessions (cookies + localStorage) captured during recording. Attach one to a test so runs start already authenticated — no password is ever stored."
      />

      {states === null ? (
        <div className="surface-card p-6 text-sm text-[#64748B]">Loading…</div>
      ) : states.length === 0 ? (
        <div className="surface-card p-12 text-center" data-testid="empty-auth-states-message">
          <KeyRound size={22} className="mx-auto text-[#94A3B8]" />
          <h2 className="font-['Geist'] font-semibold text-lg mt-3 text-[#0F172A]">No login states saved</h2>
          <p className="text-sm text-[#64748B] mt-2 max-w-md mx-auto">
            Start a recording, log into your app in the live browser, then click &quot;Save Login State&quot; in the recorder toolbar.
          </p>
        </div>
      ) : (
        <ResponsiveTable
          testId="auth-states-table"
          columns={columns}
          rows={states}
          getRowTestId={(s) => `auth-state-row-${s.id}`}
        />
      )}
    </div>
  );
}
